import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { 
  ArrowLeft, 
  User, 
  Mail, 
  Globe, 
  Calendar, 
  Trophy, 
  Star,
  Settings,
  Share2,
  Copy,
  Check,
  Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = () => {
  const { user, token, refreshUser } = useContext(AuthContext);
  const [copied, setCopied] = useState(false);
  const [showLanguageSettings, setShowLanguageSettings] = useState(false);
  const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);

  const copyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(user?.referral_code);
      setCopied(true);
      toast.success('Código copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Error al copiar el código');
    }
  };

  const updateLanguage = async (newLanguage) => {
    setIsUpdatingLanguage(true);
    
    try {
      await axios.put(`${API}/user/language`, 
        { language: newLanguage },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Refresh user data to get updated language
      await refreshUser();
      toast.success('Idioma actualizado exitosamente');
      setShowLanguageSettings(false);
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error('Error al actualizar el idioma');
    } finally {
      setIsUpdatingLanguage(false);
    }
  };

  const stats = [
    {
      label: 'Nivel Actual',
      value: user?.level || 1,
      icon: Trophy,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    {
      label: 'Experiencia',
      value: `${user?.experience_points || 0} XP`,
      icon: Star,
      color: 'text-purple-600',
      bg: 'bg-purple-100'
    },
    {
      label: 'Dificultad',
      value: user?.current_difficulty || 'Principiante',
      icon: Settings,
      color: 'text-emerald-600',
      bg: 'bg-emerald-100'
    }
  ];

  const languageNames = {
    'es': { name: 'Español', flag: '🇪🇸' },
    'en': { name: 'English', flag: '🇺🇸' },
    'fr': { name: 'Français', flag: '🇫🇷' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-yellow-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to="/dashboard"
            className="p-2 bg-white rounded-xl shadow-lg text-emerald-600 hover:text-emerald-800 transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="font-cinzel text-3xl font-bold text-emerald-800">Mi Perfil</h1>
            <p className="text-emerald-600">Gestiona tu información y configuración</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Profile Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              {/* Profile Header */}
              <div className="flex items-center gap-6 mb-8">
                <div className="w-24 h-24 bg-gradient-primary rounded-full flex items-center justify-center">
                  <User className="w-12 h-12 text-white" />
                </div>
                <div>
                  <h2 className="font-cinzel text-2xl font-bold text-emerald-800">{user?.full_name}</h2>
                  <p className="text-emerald-600 mb-2">@{user?.username}</p>
                  <div className="level-badge">
                    <Trophy className="w-4 h-4" />
                    Nivel {user?.level}
                  </div>
                </div>
              </div>

              {/* User Information */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                    <Mail className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-emerald-700 font-medium">Correo Electrónico</p>
                      <p className="text-emerald-800">{user?.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                    <Globe className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1">
                      <p className="text-sm text-emerald-700 font-medium">Idioma</p>
                      <p className="text-emerald-800">
                        {languageNames[user?.language]?.flag} {languageNames[user?.language]?.name}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowLanguageSettings(true)}
                      className="p-2 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded-lg transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                    <Calendar className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-emerald-700 font-medium">Miembro desde</p>
                      <p className="text-emerald-800">
                        {new Date(user?.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl">
                    <Settings className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-emerald-700 font-medium">Rol</p>
                      <p className="text-emerald-800 capitalize">{user?.role}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className={`w-12 h-12 ${stat.bg} rounded-xl flex items-center justify-center mx-auto mb-3`}>
                      <stat.icon className={`w-6 h-6 ${stat.color}`} />
                    </div>
                    <p className="text-2xl font-bold text-emerald-800 mb-1">{stat.value}</p>
                    <p className="text-gray-600 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Referral Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <Share2 className="w-6 h-6 text-emerald-600" />
                <h3 className="font-cinzel text-lg font-bold text-emerald-800">Programa de Referidos</h3>
              </div>
              
              <p className="text-emerald-700 text-sm mb-4 leading-relaxed">
                Invita amigos y familiares a unirse a Caminos de Ifá. Ambos recibirán beneficios especiales.
              </p>
              
              <div className="bg-emerald-50 rounded-xl p-4 mb-4">
                <p className="text-emerald-700 text-sm font-medium mb-2">Tu código de referencia:</p>
                <div className="flex items-center gap-2">
                  <code className="bg-white px-3 py-2 rounded-lg text-emerald-800 font-mono text-sm flex-1">
                    {user?.referral_code}
                  </code>
                  <button
                    onClick={copyReferralCode}
                    className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="text-center">
                <button className="btn-gold px-4 py-2 text-sm font-semibold rounded-lg w-full">
                  Compartir Enlace
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="font-cinzel text-lg font-bold text-emerald-800 mb-4">Acciones Rápidas</h3>
              
              <div className="space-y-3">
                <Link
                  to="/dashboard"
                  className="block p-3 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Trophy className="w-5 h-5 text-emerald-600" />
                    <span className="text-emerald-800 font-medium">Ver Dashboard</span>
                  </div>
                </Link>
                
                <Link
                  to="/games/orisha"
                  className="block p-3 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-emerald-600" />
                    <span className="text-emerald-800 font-medium">Jugar Orishas</span>
                  </div>
                </Link>
                
                {user?.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="block p-3 bg-yellow-50 rounded-xl hover:bg-yellow-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Settings className="w-5 h-5 text-yellow-600" />
                      <span className="text-yellow-800 font-medium">Panel de Admin</span>
                    </div>
                  </Link>
                )}
              </div>
            </div>

            {/* Progress Info */}
            <div className="bg-gradient-primary rounded-2xl p-6 text-white shadow-lg">
              <h3 className="font-cinzel text-lg font-bold mb-4">Tu Progreso</h3>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Experiencia</span>
                    <span>{user?.experience_points || 0} XP</span>
                  </div>
                  <div className="progress-bar bg-emerald-800">
                    <div 
                      className="progress-fill bg-yellow-400" 
                      style={{ width: `${Math.min(((user?.experience_points || 0) % 1000) / 10, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-emerald-200 text-xs mt-2">
                    {1000 - ((user?.experience_points || 0) % 1000)} XP para el siguiente nivel
                  </p>
                </div>
                
                <div className="text-center pt-4 border-t border-emerald-600 border-opacity-30">
                  <p className="text-emerald-200 text-sm">
                    ¡Sigue aprendiendo para desbloquear más contenido!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Language Settings Modal */}
      {showLanguageSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <Globe className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
              <h2 className="font-cinzel text-2xl font-bold text-emerald-800 mb-2">Seleccionar Idioma</h2>
              <p className="text-emerald-600 text-sm">
                Elige tu idioma preferido para la plataforma
              </p>
            </div>

            <div className="space-y-3">
              {Object.entries(languageNames).map(([code, lang]) => (
                <button
                  key={code}
                  onClick={() => updateLanguage(code)}
                  disabled={isUpdatingLanguage || user?.language === code}
                  className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                    user?.language === code
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                      : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                  } ${isUpdatingLanguage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className="text-2xl">{lang.flag}</span>
                  <span className="font-medium flex-1 text-left">{lang.name}</span>
                  {user?.language === code && (
                    <Check className="w-5 h-5 text-emerald-600" />
                  )}
                </button>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowLanguageSettings(false)}
                disabled={isUpdatingLanguage}
                className="flex-1 bg-gray-500 text-white py-3 font-semibold rounded-xl transition-all duration-300 hover:bg-gray-600 disabled:opacity-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;